import { Box, Card, CardContent, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const cardSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #EDF2F7',
  borderRadius: '8px',
};

const iconCircleSx = (color: string) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  bgcolor: '#E2E8F0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': { fontSize: 20, color },
});

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  color: '#2D3748',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.3px',
  mb: 0.5,
  display: 'block',
};

const valueSx = { fontSize: 13, fontWeight: 500, color: '#1A202C' };
const valueSecondarySx = { fontSize: 11, color: '#718096', mt: 0.25 };
const redColor = '#DC2626';
const greenColor = '#16A34A';
const iconBlue = '#3182CE';

export interface SecurityStatus {
  client2FAEnabled?: boolean;
  autoUpdateEnabled?: boolean;
  lastLoginDetailed?: { date: string; detail: string };
  lastLoginStandard?: string;
  passwordChanged?: string;
  settingsUpdated?: string;
}

interface SecurityStatusCardProps {
  data?: SecurityStatus;
}

const SecurityStatusCard = ({ data }: SecurityStatusCardProps) => {
  const securityData: SecurityStatus = data || {};


  const panelSx = {
    flex: '1 1 0',
    minWidth: 140,
    p: 1.5,
    border: '1px solid #E2E8F0',
    borderRadius: 1,
    bgcolor: '#F7FAFC',
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header: light blue shield with check + title */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ gap: 1 }}
          px={2.5}
          pt={2}
          pb={1.5}
          borderBottom="1px solid #EDF2F7"
        >
          <SecurityIcon sx={{ fontSize: 18, color: iconBlue }} />
          <Typography variant="h6" style={{ fontWeight: 600, color: '#2D3748' }}>
            Security Status
          </Typography>
        </Box>

        <Box px={2.5} py={2}>
          <Box display="flex" flexWrap="wrap" sx={{ gap: 2 }}>
            {/* 1. CLIENT 2FA – red shield with cross, "Not enabled" in red */}
            <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CancelIcon sx={{ fontSize: 20, color: redColor }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  CLIENT 2FA
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: redColor }}>
                {securityData.client2FAEnabled ? 'Enabled' : 'Not enabled'}
              </Typography>
            </Box>

            {/* 2. AUTO UPDATE – green refresh, "Enabled" in green */}
            <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: '#E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 20, color: greenColor }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  AUTO UPDATE
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: greenColor }}>
                {securityData.autoUpdateEnabled ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>

            {/* 3. LAST LOGIN (Detailed) – clock icon, date + detail line */}
            <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box sx={iconCircleSx(iconBlue)}>
                  <AccessTimeIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  LAST LOGIN
                </Typography>
              </Box>
              <Typography variant="body2" sx={valueSx}>
                {securityData.lastLoginDetailed?.date ?? '—'}
              </Typography>
              {securityData.lastLoginDetailed?.detail && (
                <Typography variant="caption" sx={valueSecondarySx}>
                  {securityData.lastLoginDetailed.detail}
                </Typography>
              )}
            </Box>

            {/* 4. LAST LOGIN (Standard) – square/arrow icon */}
            {/* <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box sx={iconCircleSx(iconBlue)}>
                  <InputIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  LAST LOGIN
                </Typography>
              </Box>
              <Typography variant="body2" sx={valueSx}>
                {securityData.lastLoginStandard}
              </Typography>
            </Box> */}

            {/* 5. PASSWORD CHANGED – key icon */}
            <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box sx={iconCircleSx(iconBlue)}>
                  <VpnKeyIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  PASSWORD CHANGED
                </Typography>
              </Box>
              <Typography variant="body2" sx={valueSx}>
                {securityData.passwordChanged}
              </Typography>
            </Box>

            {/* 6. SETTINGS UPDATED – gear icon */}
            {/* <Box sx={panelSx}>
              <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                <Box sx={iconCircleSx(iconBlue)}>
                  <SettingsIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography variant="caption" sx={labelSx}>
                  SETTINGS UPDATED
                </Typography>
              </Box>
              <Typography variant="body2" sx={valueSx}>
                {securityData.settingsUpdated}
              </Typography>
            </Box> */}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SecurityStatusCard;
